import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDownAZ,
  ArrowDownNarrowWide,
  FileSpreadsheet,
  FileText,
  FolderTree,
  Layers,
  Plus,
  Search,
  Sparkles,
} from "lucide-react";
import {
  listPlans,
  planCsvUrl,
  planPdfUrl,
  type CleanupAction,
  type CleanupPlanSummary,
} from "@/lib/api";
import { formatRelative } from "@/lib/format";
import { NewPlanDialog } from "@/components/NewPlanDialog";

type SortKey = "newest" | "oldest" | "items-desc" | "items-asc" | "scope";
type ActionFilter = "all" | CleanupAction;
type RiskFilter = "all" | "high-only";

export function PlansPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["curate-plans"],
    queryFn: () => listPlans(),
  });

  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState<ActionFilter>("all");
  const [riskFilter, setRiskFilter] = useState<RiskFilter>("all");
  const [sort, setSort] = useState<SortKey>("newest");
  const [newPlanOpen, setNewPlanOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!data) return [];
    let out = [...data];
    if (search.trim()) {
      const needle = search.trim().toLowerCase();
      out = out.filter(
        (p) =>
          p.scope.value.toLowerCase().includes(needle) ||
          p.scope.type.toLowerCase().includes(needle),
      );
    }
    if (actionFilter !== "all") {
      out = out.filter((p) => (p.summary.byAction[actionFilter] ?? 0) > 0);
    }
    if (riskFilter === "high-only") {
      out = out.filter((p) => p.summary.highRiskItems > 0);
    }
    switch (sort) {
      case "newest":
        out.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        break;
      case "oldest":
        out.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        break;
      case "items-desc":
        out.sort((a, b) => b.summary.totalItems - a.summary.totalItems);
        break;
      case "items-asc":
        out.sort((a, b) => a.summary.totalItems - b.summary.totalItems);
        break;
      case "scope":
        out.sort((a, b) => a.scope.value.localeCompare(b.scope.value));
        break;
    }
    return out;
  }, [data, search, actionFilter, riskFilter, sort]);

  const totalCount = data?.length ?? 0;
  const visibleCount = filtered.length;

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <header className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold text-curate-ink">
            Cleanup plans
          </h1>
          <p className="mt-0.5 text-sm text-curate-slate">
            Review what Curate has proposed. Nothing is irreversible until you
            approve and the cooling-off window passes.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {totalCount > 0 && (
            <div className="text-2xs text-curate-slate" aria-live="polite">
              Showing <span className="font-medium text-curate-ink">{visibleCount}</span> of{" "}
              <span className="font-medium text-curate-ink">{totalCount}</span>
            </div>
          )}
          <button
            type="button"
            data-tour-id="plans-create"
            onClick={() => setNewPlanOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-curate-primary px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-curate-primaryDark"
          >
            <Plus size={12} />
            New plan
          </button>
        </div>
      </header>

      {totalCount > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl2 border border-curate-slate/20 bg-white p-3 shadow-card">
          <div className="relative flex-1 min-w-[14rem]">
            <Search
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-curate-slate"
              aria-hidden
            />
            <input
              type="search"
              placeholder="Search by scope…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-curate-slate/30 bg-white py-1.5 pl-8 pr-3 text-sm text-curate-ink placeholder:text-curate-slate focus:border-curate-primary focus:outline-none focus:ring-2 focus:ring-curate-primary/20"
              aria-label="Search by scope"
            />
          </div>
          <Select
            label="Action"
            value={actionFilter}
            onChange={(v) => setActionFilter(v as ActionFilter)}
            options={[
              ["all", "Any action"],
              ["KEEP", "Keep"],
              ["ARCHIVE", "Archive"],
              ["CONSOLIDATE", "Consolidate"],
              ["REASSIGN_OWNER", "Reassign owner"],
              ["DELETE", "Delete"],
            ]}
          />
          <Select
            label="Risk"
            value={riskFilter}
            onChange={(v) => setRiskFilter(v as RiskFilter)}
            options={[
              ["all", "Any risk"],
              ["high-only", "High-risk only"],
            ]}
          />
          <Select
            label="Sort"
            icon={
              sort === "scope" ? (
                <ArrowDownAZ size={12} />
              ) : (
                <ArrowDownNarrowWide size={12} />
              )
            }
            value={sort}
            onChange={(v) => setSort(v as SortKey)}
            options={[
              ["newest", "Newest first"],
              ["oldest", "Oldest first"],
              ["items-desc", "Most items"],
              ["items-asc", "Fewest items"],
              ["scope", "Scope (A→Z)"],
            ]}
          />
        </div>
      )}

      {isLoading && <Skeleton />}
      {error && <ErrorBanner message={(error as Error).message} />}
      {data && totalCount === 0 && <EmptyState onCreate={() => setNewPlanOpen(true)} />}
      {data && totalCount > 0 && filtered.length === 0 && <NoMatches />}
      {filtered.length > 0 && (
        <ul data-tour-id="plans-list" className="space-y-3">
          {filtered.map((plan) => (
            <li key={plan.id}>
              <PlanRow plan={plan} />
            </li>
          ))}
        </ul>
      )}

      <NewPlanDialog open={newPlanOpen} onClose={() => setNewPlanOpen(false)} />
    </div>
  );
}

function PlanRow({ plan }: { plan: CleanupPlanSummary }) {
  const archive = plan.summary.byAction.ARCHIVE ?? 0;
  const consolidate = plan.summary.byAction.CONSOLIDATE ?? 0;
  const reassign = plan.summary.byAction.REASSIGN_OWNER ?? 0;
  const keep = plan.summary.byAction.KEEP ?? 0;

  return (
    <div className="relative rounded-xl2 border border-curate-slate/20 bg-white p-4 shadow-card transition-shadow hover:shadow-elev">
      <div className="absolute right-3 top-3 flex items-center gap-1.5">
        <a
          href={planCsvUrl(plan.id)}
          title="Download CSV (working data)"
          className="inline-flex items-center gap-1 rounded-md border border-curate-slate/30 bg-white px-2 py-1 text-2xs font-medium text-curate-ink hover:bg-curate-mist"
        >
          <FileSpreadsheet size={11} aria-hidden /> CSV
        </a>
        <a
          href={planPdfUrl(plan.id)}
          title="Download executive PDF"
          className="inline-flex items-center gap-1 rounded-md border border-curate-slate/30 bg-white px-2 py-1 text-2xs font-medium text-curate-ink hover:bg-curate-mist"
        >
          <FileText size={11} aria-hidden /> PDF
        </a>
      </div>
      <Link to={`/plans/${plan.id}`} className="block">
        <div className="flex items-start justify-between gap-4 pr-28">
          <div>
            <div className="flex items-center gap-2 text-sm">
              <FolderTree size={14} className="text-curate-slate" aria-hidden />
              <span className="font-medium text-curate-ink">
                {plan.scope.type === "all" ? "All assets" : plan.scope.value}
              </span>
              <span className="text-2xs text-curate-slate">
                · {formatRelative(plan.createdAt)}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Pill action="KEEP" count={keep} />
              <Pill action="ARCHIVE" count={archive} />
              <Pill action="CONSOLIDATE" count={consolidate} />
              <Pill action="REASSIGN_OWNER" count={reassign} />
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-2xl text-curate-ink">
              {plan.summary.totalItems}
            </div>
            <div className="text-2xs uppercase tracking-wide text-curate-slate">
              items
            </div>
            {plan.summary.highRiskItems > 0 && (
              <div className="mt-1 inline-flex items-center gap-1 rounded-full border border-curate-danger/30 bg-curate-danger/10 px-2 py-0.5 text-2xs font-medium text-curate-danger">
                <AlertTriangle size={10} aria-hidden />
                {plan.summary.highRiskItems} high-risk
              </div>
            )}
            {plan.summary.duplicateClusters > 0 && (
              <div className="mt-1 inline-flex items-center gap-1 text-2xs text-curate-slate">
                <Layers size={10} aria-hidden />
                {plan.summary.duplicateClusters} duplicate cluster
                {plan.summary.duplicateClusters > 1 ? "s" : ""}
              </div>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
  icon,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: [string, string][];
  icon?: React.ReactNode;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 text-2xs text-curate-slate">
      {icon}
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-curate-slate/30 bg-white px-2 py-1 text-2xs text-curate-ink focus:border-curate-primary focus:outline-none focus:ring-2 focus:ring-curate-primary/20"
        aria-label={label}
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}

function Pill({ action, count }: { action: CleanupAction; count: number }) {
  if (count === 0) return null;
  const label =
    action === "KEEP" ? "keep"
    : action === "ARCHIVE" ? "archive"
    : action === "CONSOLIDATE" ? "consolidate"
    : action === "REASSIGN_OWNER" ? "reassign"
    : "delete";
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-curate-mist px-2 py-0.5 text-2xs text-curate-ink">
      <span className="font-mono font-medium">{count}</span>
      {label}
    </span>
  );
}

function Skeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-24 animate-pulse rounded-xl2 bg-curate-mist/60" />
      ))}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-xl2 border border-curate-danger/30 bg-curate-danger/5 p-4 text-sm text-curate-danger">
      Couldn't load plans — {message}
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="rounded-xl2 border border-dashed border-curate-slate/30 bg-white p-10 text-center">
      <Sparkles size={28} aria-hidden className="mx-auto text-curate-primary/60" />
      <p className="mt-2 text-sm font-medium text-curate-ink">No plans yet.</p>
      <p className="mt-1 text-2xs text-curate-slate">
        Curate proposes per-asset cleanup actions by reading the Cognos audit
        DB for a folder scope. Pick a scope and we'll generate a plan for review.
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-curate-primary px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-curate-primaryDark"
      >
        <Plus size={12} />
        Create first plan
      </button>
      <p className="mt-3 text-2xs text-curate-slate/70">
        Power users: <span className="font-mono">POST /api/v1/curate/plans/from-scope</span>{" "}
        with <span className="font-mono">persist=true</span> works the same way.
      </p>
    </div>
  );
}

function NoMatches() {
  return (
    <div className="rounded-xl2 border border-dashed border-curate-slate/30 bg-white p-8 text-center">
      <p className="text-sm font-medium text-curate-ink">No plans match your filters.</p>
      <p className="mt-1 text-2xs text-curate-slate">
        Try clearing the search or widening the action / risk filters.
      </p>
    </div>
  );
}
